import {useEffect, useState} from 'react'
import {CgPathDivide} from 'react-icons/cg'
import {fetchIconAction} from '../../reducers/iconReducer'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch} from '../../store'
import {useNavigate, useParams} from 'react-router-dom'
import {
  fetchCollections,
  createCollectionAction,
  updateCollectionAction,
  collectionSelector,
} from '../../reducers/collectionReducer'
import {SHOW_SUCCESS} from '../../../util'
import InfoWrapper from '../../modules/common/SectionInfo'
import {MuiTooltip} from '../../common/Tooltip'
import {useForm, SubmitHandler, Controller} from 'react-hook-form'
import {iconListSelector} from '../../reducers/iconReducer'
import {Modal, Box} from '@mui/material'
import {KTSVG} from '../../../_metronic/helpers'

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: '#1e1e2d',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  height: 600,
  overflow: 'scroll',
}

interface FormFields {
  name: string
  icon: string
  isActive: boolean
}

const CreateCollection = () => {
  const {Id} = useParams()
  const [libraryIcon, setLibraryIcon] = useState('')
  const [isIconModalOpen, setIsIconModalOpen] = useState(false)
  const icons = useSelector(iconListSelector)
  const collections = useSelector(collectionSelector)

  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: {errors},
  } = useForm<FormFields>({
    defaultValues: {
      icon: '',
      name: '',
      isActive: false,
    },
  })

  useEffect(() => {
    dispatch(fetchIconAction())
    dispatch(fetchCollections())
  }, [dispatch])

  useEffect(() => {
    if (Id && collections.length > 0) {
      const collection = collections.find((col: any) => col.id === Number(Id))
      if (collection) {
        reset({
          name: collection.name || '',
          icon: collection.icon || '',
          isActive: collection.isActive === 1,
        })
        setLibraryIcon(collection.icon || '')
      }
    }
  }, [Id, collections, reset])

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    const finalData = {
      name: data.name,
      icon: data.icon,
      isActive: data.isActive ? 1 : 0,
    }
    
    if (Id) {
      const result = await dispatch(updateCollectionAction({data: finalData, id: Id}))
      if (updateCollectionAction.fulfilled.match(result) && result.payload?.success) {
        navigate('/collections')
      }
    } else {
      const result = await dispatch(createCollectionAction(finalData))
      if (createCollectionAction.fulfilled.match(result) && result.payload?.success) {
        navigate('/collections')
      }
    }
  }

  const handleOpenIconModal = () => {
    setIsIconModalOpen(true)
  }

  const handleCloseIconModal = () => {
    setIsIconModalOpen(false)
    setLibraryIcon('')
  }

  const handleIconSelect = (icon: string) => {
    setValue('icon', icon, { shouldValidate: true })
    handleCloseIconModal()
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          {Id ? (
            <h3 className='fw-bolder m-0'>Update Collection</h3>
          ) : (
            <h3 className='fw-bolder m-0'>Create Collection</h3>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className='form'>
        <div className='card-body border-top px-9 pb-9 pt-0'>
          <InfoWrapper bar={'down'}>
            <div className='row'>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Name</span>
                </label>
                <div className='fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Collection Name'
                    {...register('name', {required: 'Name is required'})}
                  />
                  {errors.name && (
                    <div className='text-danger mt-1'>{errors.name.message}</div>
                  )}
                </div>
              </div>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Icon</span>
                </label>
                <div className='d-flex'>
                  <Controller
                    name='icon'
                    control={control}
                    rules={{required: 'Icon is required'}}
                    render={({field}) => (
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Icon Link'
                        value={field.value || ''}
                        disabled={true}
                      />
                    )}
                  />
                  <MuiTooltip title='Add Icon from Library'>
                    <div className='col-lg-1 text-center m-auto' onClick={handleOpenIconModal}>
                      <CgPathDivide className='ms-3 pointer' size={'2rem'} />
                    </div>
                  </MuiTooltip>
                </div>
                {errors.icon && (
                  <div className='text-danger mt-1'>{errors.icon.message}</div>
                )}
              </div>
            </div>
          </InfoWrapper>
        </div>
        <div className='row mb-4 ps-10'>
          <div className='col-lg-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Active</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    {...register('isActive')}
                    className='form-check-input w-40px h-25px'
                    type='checkbox'
                    id='isActive'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='submit' className='btn btn-primary'>
            {Id ? 'Update' : 'Create'}
          </button>
        </div>
      </form>

      <Modal
        open={isIconModalOpen}
        onClose={handleCloseIconModal}
        aria-labelledby="icon-modal-title"
        aria-describedby="icon-modal-description"
      >
        <Box sx={style}>
          <div className='modal-header mb-2'>
            <h2 className='fw-bolder'>Icons</h2>
            <div
              onClick={handleCloseIconModal}
              className='btn btn-icon btn-sm btn-active-icon-primary'
              style={{ cursor: 'pointer' }}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </div>
          </div>
          <div className='card-title text-center'>
            <div className='d-flex align-items-center my-3 m-auto'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-1 position-absolute ms-6'
              />
              <input
                onChange={(e) => setLibraryIcon(e.target.value)}
                type='text'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='Search icons'
              />
            </div>
          </div>
          <div className='row mt-4'>
            {libraryIcon
              ? icons
                ?.filter((f) => f?.name?.toUpperCase().includes(libraryIcon.toUpperCase()))
                ?.map((icon: any, index: any) => (
                  <div className='col-3 rounded text-center my-1' key={index}>
                    <div
                      style={{ width: '100px', height: '50px' }}
                      className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                      onClick={() => handleIconSelect(icon.icon)}
                      data-bs-toggle='tooltip'
                      title={icon.name}
                    >
                      <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                        <img src={icon.icon} alt='icon' width={30} height={30} />
                      </p>
                    </div>
                    <div className='fs-7'>{icon.name}</div>
                  </div>
                ))
              : icons?.map((icon: any, index: any) => (
                <div className='col-3 rounded text-center my-1' key={index}>
                  <div
                    style={{ width: '100px', height: '50px' }}
                    className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                    onClick={() => handleIconSelect(icon.icon)}
                    data-bs-toggle='tooltip'
                    title={icon.name}
                  >
                    <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                      <img src={icon.icon} alt='icon' width={30} height={30} />
                    </p>
                  </div>
                  <div className='fs-7'>{icon.name}</div>
                </div>
              ))}
          </div>
        </Box>
      </Modal>
    </div>
  )
}

export default CreateCollection

